import PropTypes from 'prop-types';
import React from 'react';
import cx from 'classnames';
import { withRouter } from 'react-router';

import getUrl from '@/routes/routes/helpers/getUrl';
import { Link, LinkType } from '@letsdothis/lds/lib/candidate/Link';

import { useWindowSize } from '@/hooks/useWindowSize';
import { headerStackedListBreakpointInt } from '@/base-styles/vars';
import { getRelativeUrl } from '@/utils/routing';
import NotificationIcon from '@/shared/NotificationCenter/NotificationIcon';

import Avatar from '../Avatar';
import HeaderMenuButton from './components/HeaderMenuButton';
import HeaderMenuNav from './components/HeaderMenuNav';
import HeaderSearch from './containers/HeaderSearch';

import { hideAboveHeaderStackedListBreakpoint } from '../../base-styles/mixins.module.css';
import {
  button,
  hiddenNav,
  mask,
  notificationAndAvatarContainer,
  search,
  visibleNav,
  loginCta,
  avatarContainer,
} from './styles.module.css';

/**
 * Header menu presentational component.
 */
const HeaderMenu = ({
  categoriesVisible,
  className,
  hasUserCookie,
  navId,
  navVisible,
  onButtonClick,
  onCategoriesClick,
  onCategoriesMouseEnterOrLeave,
  onMyEventsClick,
  onMaskClick,
  onNavCloseClick,
  onNavLinkClick,
  onSearchLinkClick,
  onSearchCloseClick,
  profile,
  searchVisible,
  selectedLocale,
  userSelectedLocale,
  narrow,
  location,
  ...routerProps
}) => {
  const { width: windowWidth } = useWindowSize();
  const showNotificationIcon = windowWidth <= headerStackedListBreakpointInt;
  const inOrganiserView = !!(routerProps?.routes || []).find(
    (route) => route.inOrganiserView,
  );
  const inLDTView = !!(routerProps?.routes || []).find(
    (route) => route.inLDTView,
  );

  return (
    <div className={className}>
      {searchVisible && !inOrganiserView && !inLDTView && (
        <div>
          <HeaderSearch
            onSearchCloseClick={onSearchCloseClick}
            searchVisible={searchVisible}
          />
        </div>
      )}
      {navVisible && (
        <>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <button type="button" className={mask} onClick={onMaskClick} />
        </>
      )}
      <HeaderMenuButton
        className={button}
        navId={navId}
        navVisible={navVisible}
        onClick={onButtonClick}
      />
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div className={search} onClick={onSearchLinkClick} />
      <HeaderMenuNav
        categoriesVisible={categoriesVisible}
        className={navVisible ? visibleNav : hiddenNav}
        id={navId}
        hasUserCookie={hasUserCookie}
        onCategoriesClick={onCategoriesClick}
        onCategoriesMouseEnterOrLeave={onCategoriesMouseEnterOrLeave}
        onMyEventsClick={onMyEventsClick}
        onLinkClick={onNavLinkClick}
        onCloseClick={onNavCloseClick}
        expanded={navVisible}
        onSearchLinkClick={onSearchLinkClick}
        profile={profile}
        selectedLocale={selectedLocale}
        userSelectedLocale={userSelectedLocale}
        narrow={narrow}
        inOrganiserView={inOrganiserView}
        inLDTView={inLDTView}
        organiserId={routerProps?.params?.organiserId}
      />
      {profile && showNotificationIcon && (
        <div className={notificationAndAvatarContainer}>
          <NotificationIcon />
          <div className={avatarContainer}>
            <Avatar user={profile} small trackingSource="header_menu" />
          </div>
        </div>
      )}
      {!profile && (
        <div className={cx(hideAboveHeaderStackedListBreakpoint, loginCta)}>
          <Link
            type={LinkType.Internal}
            href={getUrl.loginPage({
              hash: { source: 'mobile_navbar' },
              query: { returnUrl: getRelativeUrl(location) },
            })}
          >
            Sign up
          </Link>
        </div>
      )}
    </div>
  );
};

HeaderMenu.propTypes = {
  categoriesVisible: PropTypes.bool,
  className: PropTypes.string,
  hasUserCookie: PropTypes.bool,
  navId: PropTypes.string,
  navVisible: PropTypes.bool,
  onButtonClick: PropTypes.func,
  onCategoriesClick: PropTypes.func,
  onCategoriesMouseEnterOrLeave: PropTypes.func,
  onMyEventsClick: PropTypes.func,
  onMaskClick: PropTypes.func,
  onNavCloseClick: PropTypes.func,
  onNavLinkClick: PropTypes.func,
  onSearchLinkClick: PropTypes.func,
  onSearchCloseClick: PropTypes.func,
  profile: PropTypes.shape(),
  searchVisible: PropTypes.bool,
  selectedLocale: PropTypes.string,
  narrow: PropTypes.bool,
  location: PropTypes.shape(),
};

HeaderMenu.defaultProps = {
  categoriesVisible: false,
  className: '',
  hasUserCookie: false,
  navId: 'menuNav',
  navVisible: false,
  onButtonClick: () => {},
  onCategoriesClick: () => {},
  onCategoriesMouseEnterOrLeave: () => {},
  onMyEventsClick: () => {},
  onMaskClick: () => {},
  onNavCloseClick: () => {},
  onNavLinkClick: () => {},
  onSearchLinkClick: () => {},
  onSearchCloseClick: () => {},
  profile: undefined,
  searchVisible: false,
  selectedLocale: undefined,
  narrow: false,
  location: {},
};

export default withRouter(HeaderMenu);
